import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  OnInit,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlContainer,
  FormControl,
  FormGroupDirective,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { FormInputComponent } from '../form-input.component';
import { ErrorMessageComponent } from '../../../layout/error-message/error-message.component';
import { AppendTestIdPipe } from '../../../../pipes/append-test-id/append-test-id.pipe';
import { CmsTextProviderService } from '../../../../services/cms-text-provider.service';
import { ButtonComponent } from '../../../feedback/button/button.component';

export type ListInputItemType = 'number' | 'text';
export type ListInputItemValue = number | string;
type SortDirection = 'ascending' | 'descending';

interface ListInputEntry {
  index: number;
  value: ListInputItemValue;
}

@Component({
  selector: 'aw-list-input',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ErrorMessageComponent,
    AppendTestIdPipe,
    ButtonComponent,
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  templateUrl: './list-input.component.html',
})
export class ListInputComponent extends FormInputComponent implements OnInit {
  public readonly itemType: InputSignal<ListInputItemType> =
    input<ListInputItemType>('text');
  public readonly searchable: InputSignal<boolean> = input(false);
  public readonly searchPlaceholder: InputSignal<string> = input('Suchen');
  public readonly listLabel: InputSignal<string> = input('Liste');
  public readonly itemPlaceholder: InputSignal<string> = input('Zeileninhalt');

  public readonly searchControl: FormControl<string> = new FormControl<string>(
    '',
    { nonNullable: true }
  );
  public readonly itemControl: FormControl<ListInputItemValue | null> =
    new FormControl<ListInputItemValue | null>(null);
  public readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );

  public readonly isEditing: WritableSignal<boolean> = signal(false);
  public readonly editingIndex: WritableSignal<number | undefined> =
    signal(undefined);
  public readonly sortDirection: WritableSignal<SortDirection | undefined> =
    signal('ascending');

  private readonly items: WritableSignal<ListInputItemValue[]> = signal([]);
  private readonly searchTerm: WritableSignal<string> = signal('');

  public readonly visibleEntries: Signal<ListInputEntry[]> = computed(() => {
    const searchTerm: string = this.searchTerm().trim().toLocaleLowerCase();
    const entries: ListInputEntry[] = this.items().map(
      (value: ListInputItemValue, index: number) => ({ index, value })
    );

    if (!this.searchable() || !searchTerm) {
      return entries;
    }

    return entries.filter(({ value }: ListInputEntry) =>
      value.toString().toLocaleLowerCase().includes(searchTerm)
    );
  });

  constructor(public override ngControl: NgControl) {
    super(ngControl);
  }

  ngOnInit(): void {
    this.subscription.add(
      this.searchControl.valueChanges.subscribe((value: string) =>
        this.searchTerm.set(value)
      )
    );
    this.subscription.add(
      this.ngControl.control!.valueChanges.subscribe((value: unknown) =>
        this.syncItems(value)
      )
    );
    this.subscription.add(
      this.ngControl.control!.statusChanges.subscribe(() =>
        this.syncInternalControlState()
      )
    );

    this.syncItems(this.ngControl.control!.value);
    this.syncInternalControlState();
  }

  public startAdding(): void {
    if (this.ngControl.control!.disabled || this.isEditing()) {
      return;
    }

    this.editingIndex.set(undefined);
    this.itemControl.setValue(null);
    this.isEditing.set(true);
  }

  public startEditing(entry: ListInputEntry): void {
    if (this.ngControl.control!.disabled || this.isEditing()) {
      return;
    }

    this.editingIndex.set(entry.index);
    this.itemControl.setValue(entry.value);
    this.isEditing.set(true);
  }

  public saveItem(): void {
    const value: ListInputItemValue | undefined = this.normalizedItemValue();
    if (value === undefined) {
      this.itemControl.markAsTouched();
      return;
    }

    const updatedItems: ListInputItemValue[] = [...this.items()];
    const index: number | undefined = this.editingIndex();
    if (index === undefined) {
      updatedItems.push(value);
    } else {
      updatedItems[index] = value;
    }

    this.updateItems(updatedItems);
    this.cancelEditing();
  }

  public cancelEditing(): void {
    this.isEditing.set(false);
    this.editingIndex.set(undefined);
    this.itemControl.setValue(null);
  }

  public deleteItem(index: number): void {
    if (this.ngControl.control!.disabled || this.isEditing()) {
      return;
    }

    const updatedItems: ListInputItemValue[] = this.items().filter(
      (_item: ListInputItemValue, itemIndex: number) => itemIndex !== index
    );
    this.updateItems(updatedItems);
  }

  public toggleSortDirection(): void {
    if (this.ngControl.control!.disabled || this.isEditing()) {
      return;
    }

    this.sortDirection.update((direction: SortDirection | undefined) =>
      direction === 'ascending' ? 'descending' : 'ascending'
    );
    this.updateItems(this.items());
  }

  public handleItemKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveItem();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEditing();
    }
  }

  public hasValidItemValue(): boolean {
    return this.normalizedItemValue() !== undefined;
  }

  private syncItems(value: unknown): void {
    const values: ListInputItemValue[] = Array.isArray(value)
      ? value.filter(
          (item: unknown): item is ListInputItemValue =>
            typeof item === 'string' || typeof item === 'number'
        )
      : [];
    const sortedValues: ListInputItemValue[] = this.sortedItems(values);

    // Sorting the presentation must not turn form initialization into a change.
    this.items.set(sortedValues);
  }

  private updateItems(items: ListInputItemValue[]): void {
    const sortedItems: ListInputItemValue[] = this.sortedItems(items);
    this.items.set(sortedItems);
    this.ngControl.control!.setValue(sortedItems);
    this.ngControl.control!.markAsTouched();
    this.ngControl.control!.markAsDirty();
  }

  private sortedItems(items: ListInputItemValue[]): ListInputItemValue[] {
    if (this.sortDirection() === undefined) {
      return [...items];
    }

    const directionFactor: number =
      this.sortDirection() === 'ascending' ? 1 : -1;

    return [...items].sort(
      (first: ListInputItemValue, second: ListInputItemValue) => {
        if (this.itemType() === 'number') {
          return (Number(first) - Number(second)) * directionFactor;
        }

        return (
          first.toString().localeCompare(second.toString(), undefined, {
            numeric: true,
            sensitivity: 'base',
          }) * directionFactor
        );
      }
    );
  }

  private normalizedItemValue(): ListInputItemValue | undefined {
    const value: ListInputItemValue | null = this.itemControl.value;

    if (this.itemType() === 'number') {
      if (value === null || value === '') {
        return undefined;
      }

      const numberValue: number = Number(value);
      return Number.isNaN(numberValue) ? undefined : numberValue;
    }

    const textValue: string = value?.toString().trim() ?? '';
    return textValue || undefined;
  }

  private syncInternalControlState(): void {
    if (this.ngControl.control!.disabled) {
      this.searchControl.disable({ emitEvent: false });
      this.itemControl.disable({ emitEvent: false });
      return;
    }

    this.searchControl.enable({ emitEvent: false });
    this.itemControl.enable({ emitEvent: false });
  }
}
